"use client";

import React from "react";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { ErrorBoundary } from "./_components/error-boundary";
import { type ContentPage } from "@/app/array/_components/dialog-add-bom";
import { type DirectOnFinish } from "./_components/dialog-add-bom-types";
import { useClientAuth } from "@/app/providers/client-auth";
import { listCatalogParts, listCatalogRoofAttachments } from "@/lib/api/client/catalog";
import { getActivePartConfiguration } from "@/lib/api/client/parts-config";
import { listProjects } from "@/lib/api/client/projects";
import { listModules } from "@/lib/api/client/modules";
import { getOrganizationById } from "@/lib/api/client/organizations";
import PageLoaderOverlay from "@/components/page-loader-overlay";

const CanvasDialogShell = dynamic(() => import("./_components/canvas-dialog-shell"), {
  ssr: false,
  loading: () => <PageLoaderOverlay message="Loading design page..." opacity="dark" />,
});

export function ArrayPageClient() {
  const searchParams = useSearchParams();
  const { status, data } = useClientAuth();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [payload, setPayload] = React.useState<{
    parts: any[];
    modules: any[];
    attachments: any[];
    projects: any[];
    partConfig: any;
    organizationId: string;
    initContent: string;
    directOnFinish?: DirectOnFinish;
  } | null>(null);

  React.useEffect(() => {
    let active = true;

    async function load() {
      try {
        if (!data) return;
        setLoading(true);
        setError(null);

        const organizationId = searchParams.get("orgId") || searchParams.get("o") || data.activeOrganization.id;
        const initContent = searchParams.get("i") || "initial";
        const directOnFinish = (searchParams.get("onfinish") || undefined) as DirectOnFinish | undefined;

        await getOrganizationById(organizationId);

        const partConfigPromise = getActivePartConfiguration().catch(() => null);

        const [modules, parts, attachments, partConfig, projectsResponse] = await Promise.all([
          listModules(),
          listCatalogParts(),
          listCatalogRoofAttachments(),
          partConfigPromise,
          initContent === "new-project"
            ? Promise.resolve({ data: [] as any[] })
            : listProjects({
                organizationId,
                page: 1,
                pageSize: 100,
                sort: "createdAt:desc",
              }),
        ]);

        if (!active) return;
        setPayload({
          parts,
          modules,
          attachments,
          projects: projectsResponse.data || [],
          partConfig: partConfig?.config,
          organizationId,
          initContent,
          directOnFinish,
        });
      } catch (cause: any) {
        if (!active) return;
        setError(cause?.message ?? "Failed to load configuration tool");
      } finally {
        if (active) setLoading(false);
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [data, searchParams]);

  if (status !== "authenticated" || !data || loading) {
    return <PageLoaderOverlay message="Loading design page..." opacity="dark" />;
  }

  if (error || !payload) {
    return <div className="p-4 text-sm text-red-500">{error ?? "Failed to load configuration tool."}</div>;
  }

  const user = {
    id: data.user.id,
    email: data.user.email,
    firstName: data.user.firstName,
    lastName: data.user.lastName,
    fullName: data.user.fullName,
    organizationId: data.activeOrganization.id,
    organization: data.activeOrganization,
    isAdmin: data.activeOrganization.isAdmin,
    organizations: data.organizations,
    isSnapNrack: !!data.user.isPlatformAdmin,
    termsVersion: data.user.termsVersion,
  };

  const serializedProjects = payload.projects.map((project) => ({
    id: project.id,
    name: project.name,
    organizationId: project.organizationId,
  }));

  return (
    <ErrorBoundary>
      <CanvasDialogShell
        initContent={payload.initContent as ContentPage}
        directOnFinish={payload.directOnFinish}
        organizationId={payload.organizationId}
        user={user as any}
        projects={serializedProjects as any}
        parts={payload.parts}
        modules={payload.modules}
        attachments={payload.attachments}
        partConfig={payload.partConfig}
      />
    </ErrorBoundary>
  );
}
